// Generic in-game help message modal.
//
// Displays a short informational message with a "Don't show this again"
// checkbox (checked by default) and a "Got it" dismiss button. The host
// component owns show/hide and persistence; this component just fires a
// `dismiss` CustomEvent with `{ dontShowAgain: boolean }` in detail.
//
// Styling mirrors confirm-delete.js so help modals look like the rest of
// the Bootstrap-themed UI.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

class HelpMessage extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    heading: { type: String },
    /** Stable identifier for callers — this component doesn't persist it. */
    helpId: { type: String, attribute: 'help-id' },
    _dontShowAgain: { state: true },
  };

  constructor() {
    super();
    this.show = false;
    this.heading = '';
    this.helpId = '';
    this._dontShowAgain = true;
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  static styles = css`
    :host {
      display: contents;
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1050;
    }

    .modal-dialog {
      max-width: 500px;
      width: 100%;
      margin: 1rem;
    }

    .modal-content {
      background-color: white;
      border-radius: 0.5rem;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      border-bottom: 1px solid #dee2e6;
    }

    .modal-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 500;
    }

    .btn-close {
      padding: 0.5rem;
      background: transparent;
      border: none;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      opacity: 0.5;
    }

    .btn-close:hover {
      opacity: 0.75;
    }

    .modal-body {
      padding: 1rem;
    }

    .modal-body ::slotted(p) {
      margin: 0 0 0.75rem 0;
    }

    .modal-body ::slotted(p:last-child) {
      margin-bottom: 0;
    }

    .modal-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem;
      border-top: 1px solid #dee2e6;
    }

    .form-check {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      user-select: none;
      font-size: 0.95rem;
    }

    .form-check-input {
      margin: 0;
      cursor: pointer;
    }

    .btn {
      padding: 0.5rem 1rem;
      border-radius: 0.375rem;
      border: 1px solid transparent;
      font-size: 1rem;
      cursor: pointer;
      transition:
        background-color 0.15s,
        border-color 0.15s;
    }

    .btn-primary {
      background-color: #0d6efd;
      border-color: #0d6efd;
      color: white;
    }

    .btn-primary:hover {
      background-color: #0b5ed7;
      border-color: #0a58ca;
    }
  `;

  /** @param {KeyboardEvent} e */
  _onKeyDown(e) {
    if (e.key === 'Escape' && this.show) {
      e.stopImmediatePropagation();
      this._handleDismiss();
    }
  }

  /** @param {Map<string, unknown>} changed */
  updated(changed) {
    if (changed.has('show')) {
      if (this.show) {
        document.addEventListener('keydown', this._onKeyDown, true);
      } else {
        document.removeEventListener('keydown', this._onKeyDown, true);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown, true);
  }

  _handleDismiss() {
    this.dispatchEvent(
      new CustomEvent('dismiss', {
        bubbles: true,
        composed: true,
        detail: { dontShowAgain: this._dontShowAgain },
      }),
    );
  }

  _handleCheckbox(e) {
    this._dontShowAgain = /** @type {HTMLInputElement} */ (e.target).checked;
  }

  render() {
    if (!this.show) {
      return html``;
    }

    return html`
      <div class="modal-backdrop" @click="${this._handleDismiss}">
        <div
          class="modal-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="help-message-title"
          @click="${(/** @type {Event} */ e) => e.stopPropagation()}"
        >
          <div class="modal-content">
            <div class="modal-header">
              <h5 id="help-message-title" class="modal-title">${this.heading}</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="${this._handleDismiss}">
                &times;
              </button>
            </div>
            <div class="modal-body">
              <slot></slot>
            </div>
            <div class="modal-footer">
              <label class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  .checked="${this._dontShowAgain}"
                  @change="${this._handleCheckbox}"
                />
                Don't show this again
              </label>
              <button type="button" class="btn btn-primary" @click="${this._handleDismiss}">Got it</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('help-message', HelpMessage);
